import React, { useState } from "react";
import { SiDiscord } from "react-icons/si";
import { FiExternalLink, FiPlay } from "react-icons/fi";
import "./ZombsRoyale.css";

const RULES = [
  {
    num: "I",
    text: <><strong>No Teaming / Tac Avoidance</strong> of any kind! If you've been caught teaming in any ZFT Event, all your wins will be <strong>VOIDED</strong>.</>,
  },
  {
    num: "II",
    text: <><strong>Cosmetics and Emotes / Sprays are ALLOWED</strong> unless told otherwise by Staff.</>,
  },
  {
    num: "III",
    text: <><strong>Banned Players Are Prohibited</strong>, including alt accounts. Hacks and Macros are banned as well. If a clan is seen using a banned player in ANY way, the entire clan's win will be <strong>VOIDED</strong>.</>,
  },
  {
    num: "IV",
    text: <><strong>7 Duos Limit / 3 Squads Limit</strong> per clan. Clans are responsible for their members' actions!</>,
  },
  {
    num: "V",
    text: <><strong>Name Rule:</strong> You must use your name as it appears in the server. If your name cannot be pinged, it is an automatic void. You may use <strong>/eventname</strong> to register a different display name. No vertical names — make your tag clearly separate (e.g. <strong>[sm] AP</strong>, not <strong>smAP</strong>).</>,
  },
  {
    num: "VI",
    text: <><strong>No Storm Healing!</strong> You may only use heals already in your inventory when storm healing. Picking up heals in storm and using them will get your wins <strong>VOIDED</strong>. If all players in final circle die and you didn't exit the storm, it also results in a void (exception: if you killed them yourself while in storm).</>,
  },
  {
    num: "VII",
    text: <>Using any <strong>Racist, Hateful, Vulgar, or Insensitive</strong> name or language in any capacity will result in your win being <strong>VOIDED</strong>.</>,
  },
  {
    num: "VIII",
    special: true,
    label: "Snek Wars Special Rule",
    text: <><strong>Event Rosters Rule:</strong> Your clan/team must post a roster message in <strong>#event-rosters</strong> before the event starts. Roster changes are only allowed if a player leaves and needs a replacement. Rosters posted after a game starts will have wins <strong>VOIDED</strong>.</>,
  },
];

const LOBBY_CODES = [
  { region: "US East",  solos: "zftusesolos",  duos: "zftuseduos",  squads: "zftusesquads" },
  { region: "US West",  solos: "zftuswsolos",  duos: "zftuswduos",  squads: "zftuswsquads" },
  { region: "Europe",   solos: "zfteusolos",   duos: "zfteuduos",   squads: "zfteusquads"  },
  { region: "Asia",     solos: "zftasiasolos", duos: "zftasiaduos", squads: "zftasiasquads" },
];

const TUTORIAL_VIDEO = "/Customs_Tutorial.mp4";

const ZombsRoyale = () => {
  const [tutorialPlaying, setTutorialPlaying] = useState(false);

  return (
  <div className="zr-page">

    {/* ── Hero ── */}
    <section className="zr-hero">
      <video
        className="zr-hero-video"
        src="https://storage.endext.com/end-web/zombsroyale-gameplay.mp4?cache=1"
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
      />
      <div className="zr-hero-overlay" aria-hidden="true" />
      <div className="container">
        <div className="zr-hero-content">
          <h1 className="zr-hero-title font-display">
            ZombsFight<span className="text-gradient-gold">Fi</span>
          </h1>
          <p className="zr-hero-sub">
            The premier destination for competitive ZombsRoyale events. Weekly tournaments
            across every region — Solos, Duos, Squads, and more.
          </p>
          <div className="zr-hero-actions">
            <a
              href="https://discord.com/invite/98T6NeC4vQ"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-lg"
            >
              <SiDiscord size={20} /><span>Join the Discord</span>
            </a>
            <a href="/tournaments" className="btn btn-ghost btn-lg">
              <span>Browse Tournaments</span><FiExternalLink size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ── How to Join ── */}
    <section className="zr-section zr-section-alt">
      <div className="container">
        <div className="zr-section-header">
          <h2 className="zr-section-title font-display">How to Join Events</h2>
          <p className="zr-section-subtitle">Everything you need to participate in ZFT events</p>
        </div>
        <div className="zr-join-grid">

          <div className="zr-glass-card zr-join-card">
            <h3>Public Lobbies</h3>
            <p>
              No lobby code needed. The Event Host posts a countdown in{" "}
              <strong style={{ color: "var(--text-primary)" }}>#event-countdowns</strong> with
              the region and game mode — just queue up at the right time.
            </p>
            <ol className="zr-steps">
              <li className="zr-step">
                <span className="zr-step-num">1</span>
                <span className="zr-step-text">Watch <strong>#event-countdowns</strong> for the event announcement — it'll specify the region and game mode.</span>
              </li>
              <li className="zr-step">
                <span className="zr-step-num">2</span>
                <span className="zr-step-text">Open ZombsRoyale and switch to the <strong>correct region and game mode</strong> specified by the host.</span>
              </li>
              <li className="zr-step">
                <span className="zr-step-num">3</span>
                <span className="zr-step-text">Ready up when the Event Host gives the countdown. <strong>Late readying = missed game.</strong></span>
              </li>
              <li className="zr-step">
                <span className="zr-step-num">4</span>
                <span className="zr-step-text">Your in-game name must match your <strong>Discord display name</strong> (or your registered /eventname).</span>
              </li>
            </ol>
          </div>

          <div className="zr-glass-card zr-join-card">
            <h3>Custom Lobbies</h3>
            <p>
              Open <strong style={{ color: "var(--text-primary)" }}>Settings</strong> on the ZombsRoyale main page,
              press <strong style={{ color: "var(--text-primary)" }}>Join Tournament</strong>, and enter the code listed
              in <strong style={{ color: "var(--text-primary)" }}>#event-countdowns</strong>.
            </p>
            <div className="zr-codes-table-wrap"><table className="zr-codes-table">
              <thead>
                <tr><th>Region</th><th>Solos</th><th>Duos</th><th>Squads</th></tr>
              </thead>
              <tbody>
                {LOBBY_CODES.map((r) => (
                  <tr key={r.region}>
                    <td>{r.region}</td>
                    <td><span className="zr-code">{r.solos}</span></td>
                    <td><span className="zr-code">{r.duos}</span></td>
                    <td><span className="zr-code">{r.squads}</span></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

        </div>

        <div className="zr-glass-card zr-video-card">
          <div className="zr-video-label">Custom Lobby Tutorial</div>
          <div className="zr-video-wrap">
            {tutorialPlaying ? (
              <video controls autoPlay>
                <source src={TUTORIAL_VIDEO} type="video/mp4" />
              </video>
            ) : (
              <div className="zr-video-thumb" onClick={() => setTutorialPlaying(true)}>
                <div className="zr-play-btn" aria-label="Play tutorial">
                  <FiPlay size={30} />
                </div>
                <span className="zr-thumb-label">Custom Lobby Tutorial</span>
                <span className="zr-thumb-sub">Click to play</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>

    {/* ── Event Rules ── */}
    <section className="zr-section">
      <div className="container">
        <div className="zr-section-header">
          <h2 className="zr-section-title font-display">Event Rules</h2>
          <p className="zr-section-subtitle">All participants must follow these rules at every ZFT event</p>
        </div>
        <div className="zr-rules-grid">
          {RULES.map((r) => (
            <div key={r.num} className={`zr-glass-card zr-rule-card${r.special ? " special" : ""}`}>
              <span className="zr-rule-numeral">{r.num}</span>
              <div className="zr-rule-body">
                {r.special && <div className="zr-rule-label">{r.label}</div>}
                <p className="zr-rule-text">{r.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "2rem" }}>
          Failure to follow these rules can result in a mute, kick, ban, or clan blacklisting.
          DM a ZFT Manager with any questions before events.
        </p>
      </div>
    </section>

    {/* ── Events Calendar ── */}
    <section className="zr-section zr-section-alt">
      <div className="container">
        <div className="zr-section-header">
          <h2 className="zr-section-title font-display">Events Calendar</h2>
          <p className="zr-section-subtitle">Stay up-to-date with all upcoming ZFT tournaments and matches</p>
        </div>
        <div className="zr-glass-card zr-calendar-card">
          <div className="zr-calendar-embed">
            <iframe
              src="https://calendar.google.com/calendar/embed?src=crowngamingmanagement@gmail.com&ctz=America/New_York&mode=WEEK&bgcolor=%230f0f0f&showTitle=0&showTabs=0&showCalendars=0&showPrint=0&showTz=0"
              title="ZFT Events Calendar"
              frameBorder="0"
              style={{ pointerEvents: "none" }}
            />
          </div>
          <a
            className="zr-calendar-link"
            href="https://calendar.google.com/calendar/embed?src=crowngamingmanagement@gmail.com&ctz=America/New_York"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiExternalLink size={14} /> Open in Google Calendar
          </a>
        </div>
      </div>
    </section>

  </div>
  );
};

export default ZombsRoyale;
